import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, Button, Checkbox, FormControlLabel, TextField } from '@mui/material'
import { toast } from 'react-toastify'
import { Modal } from 'antd'
import {
  getGrupos,
  getListas,
  calcularPlano,
  getPlanoLineas,
  getGrupoMargenes,
  setGrupoMargen,
  setGrupoMargenesMasivo,
  getGrupoZdpc,
  setGrupoZdpc,
  enviarPlanoSap,
  getEnviosSap,
  getEnvioSapLineas,
  reenviarEnvioSap,
} from 'src/services/preciosService'
import TablaPrecios from './TablaPrecios'
import MargenTarjetas from './MargenTarjetas'
import MargenGrilla from './MargenGrilla'
import { descargarXlsx, descargarTxt, construirTxt, validarLineas, agruparPorTabla } from './planoExport'
import {
  margenesEditables as editablesDe,
  faltaMargen,
  pvpEfectivoDe,
  etiquetaMoto,
  leerPct,
  esPctValido,
  construirGrilla,
  aplicarAColumna,
  cambiosAItems,
  enLotes,
  resumirLotes,
  sinMargenPorMoto,
  lineasSinMargen,
  lineasSinMargenUna,
  fraccion,
  mismaSeleccion,
  partirPorFecha,
  calcularVarias,
} from './margenesMasivo'

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`
const pct = (v) => `${(Number(v || 0) * 100).toFixed(1).replace('.0', '')}%`

/**
 * Workbench de construcción del plano.
 *
 * La idea: que se entienda sin manual. Cuatro pasos numerados, de arriba abajo:
 *   1. elegís la moto (modelo + año = "conca"; los colores van solos)
 *   2. ves el PVP y el IVA que puso Trade
 *   3. ves CÓMO se calcula, en palabras
 *   4. ves EXACTAMENTE qué se va a cargar en cada tabla de SAP
 *
 * Con UNA moto es el trámite de siempre, con tarjetas. Con dos o más, los márgenes pasan a una
 * grilla moto x clasificación y el cálculo corre moto por moto: mismo plano, sin repetir N veces.
 */
const PlanoWorkbench = ({
  onChanged, preGrupoIds = null, onContinuar = null, soloGrupos = null,
}) => {
  const [grupos, setGrupos] = useState([])
  const [listas, setListas] = useState([])
  // Las motos elegidas. Con una sola, `sel` es esa y todo funciona como antes; con varias, `sel` es
  // null y manda el modo de varias motos.
  const [sels, setSels] = useState([])
  const sel = sels.length === 1 ? sels[0] : null
  const multi = sels.length > 1
  const [org, setOrg] = useState('1000')
  const [lineas, setLineas] = useState([])
  const [resumen, setResumen] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  // formato del TXT de LSMW: tabulación es lo que sale al guardar el Excel como .txt (pendiente confirmar)
  const [delim, setDelim] = useState('\t')
  const [cols, setCols] = useState('AF')
  // los otros dos supuestos del TXT: estaban parametrizados en el exportador pero fijos en el código
  const [cabecera, setCabecera] = useState(true)
  const [bom, setBom] = useState(false)
  const [verTxt, setVerTxt] = useState(false)
  // Por defecto solo se ven las motos del ciclo de Trade. Desmarcando se puede armar el plano de
  // CUALQUIER moto que ya tenga precio definido, sin esperar una propuesta nueva.
  const [soloTrade, setSoloTrade] = useState(true)
  // Qué archivos se van a bajar. Se elige al DESCARGAR, no al calcular: calcular siempre genera todo
  // para poder comparar el plano completo. Dejar elegir qué se calcula haría fácil generar un plano
  // parcial y creerlo completo — y eso llega a SAP. Caso real: la carga de 807 falló y las otras 6 ya
  // entraron; no hay por qué volver a bajar las 7.
  const [tablasOff, setTablasOff] = useState({})
  // El margen vive en la MOTO, no en la clasificación: acá se ve y se edita el de cada condición
  // mientras se arma el plano, que es el momento en que se nota que falta.
  const [margenes, setMargenes] = useState([])
  const [edits, setEdits] = useState({})
  const [guardandoM, setGuardandoM] = useState(false)
  // Una moto: si la lectura de sus márgenes falló (o todavía no llegó), no se sabe qué falta, y eso
  // frena la descarga y el «Continuar» igual que un margen vacío.
  const [falloSingle, setFalloSingle] = useState(false)
  const [cargandoSingle, setCargandoSingle] = useState(false)
  const turnoSingle = useRef(0)
  // Grupos de descuento (816 ZDPC): los maneja Back Comercial, no Trade. Deciden qué líneas EXISTEN,
  // así que van acá, junto al armado, y no en la pantalla del precio.
  const [zdpc, setZdpc] = useState([])
  // El % de cada grupo de descuento se edita acá mismo. Vacío = no se tocó.
  const [zdpcEdits, setZdpcEdits] = useState({})
  const [guardandoZdpc, setGuardandoZdpc] = useState(0)
  // Varias motos: los márgenes de cada una ([{ grupo, margenes, fallo }]), lo tecleado por celda
  // (moto_lista), el % de «Aplicar a todas» por columna y el resultado del cálculo de cada moto.
  const [margenesMulti, setMargenesMulti] = useState([])
  const [editsMulti, setEditsMulti] = useState({})
  const [aplicarTxt, setAplicarTxt] = useState({})
  const [soloVacias, setSoloVacias] = useState(false)
  const [cargandoMulti, setCargandoMulti] = useState(false)
  const [resultadosMulti, setResultadosMulti] = useState([])
  const [progreso, setProgreso] = useState(null)
  // Envío a SAP por API (f109). Convive con las descargas: el XLSX/TXT sigue para carga manual.
  const [enviosSap, setEnviosSap] = useState([])
  const [loteSap, setLoteSap] = useState(null)
  const [enviandoSap, setEnviandoSap] = useState(false)
  const [rechazosSap, setRechazosSap] = useState({})
  // Cada carga de márgenes lleva su turno: si cambian las motos a mitad de camino, la respuesta
  // vieja se descarta en vez de pisar la grilla de la selección nueva.
  const turnoMulti = useRef(0)
  const preAplicado = useRef('')
  // La selección VIGENTE, para lo que termina después de un await: un cálculo o una recarga que
  // arrancó con otras motos no puede usar la selección que tenía en su closure.
  const selsRef = useRef(sels)
  selsRef.current = sels
  const orgRef = useRef(org)
  orgRef.current = org

  const fetchBase = async () => {
    setIsLoading(true)
    const [g, l] = await Promise.all([getGrupos(), getListas()])
    if (g && g.data) setGrupos(g.data)
    if (l && l.data) setListas(l.data)
    setIsLoading(false)
  }

  useEffect(() => {
    fetchBase()
  }, [])

  /**
   * Solo las clasificaciones que USAN margen: 'base' es el PVP sin impuestos y 'pvp' es el PVP, no
   * tienen margen que poner. La formula sale del catalogo de listas, que ya esta cargado.
   */
  const margenesEditables = useMemo(() => editablesDe(margenes, listas), [margenes, listas])

  /**
   * Las condiciones que no resuelven NINGÚN margen: el motor no encontró regla de esta moto para esa
   * clasificación (desde f80 no se hereda de otra), así que la línea saldría sin margen. Con el margen viviendo en la moto, eso es un dato que falta, no un caso por defecto, y
   * por eso frena el armado del plano en vez de dejarlo pasar con un número inventado.
   *
   * Un margen cargado en 0 % NO entra acá: cero es una decisión que alguien tomó, no un vacío.
   */
  const sinMargen = useMemo(() => margenesEditables.filter(faltaMargen), [margenesEditables])

  /**
   * Las condiciones que REALMENTE van a generar línea, con el margen de esta moto.
   *
   * Antes esto se mostraba dos veces y con datos distintos: un listado de chips «se van a generar
   * líneas para estas N condiciones» con el margen GLOBAL de cada clasificación, y más abajo, después
   * de calcular, otro bloque con el margen de ESTA moto y editable. Dos listas del mismo dato con
   * números que podían no coincidir. Quedó una sola, acá, editable desde el arranque.
   *
   * `aplica` ya resuelve el caso ZDPC: solo entra si la moto está marcada en ese grupo de descuento.
   * Los grupos no marcados no listan acá porque tienen su propio panel abajo, con su checkbox.
   */
  const margenesAplican = useMemo(
    () => margenes
      .filter((m) => Number(m.aplica) === 1)
      .sort((a, b) => String(a.tabla_sap).localeCompare(String(b.tabla_sap))),
    [margenes],
  )

  /**
   * El precio con el que se va a armar el plano: la propuesta de Trade gana sobre el vigente, igual que
   * en sp_precios_plano_calcular. Para una moto NUEVA el vigente es 0 y solo existe la propuesta: si se
   * mira solo el vigente, el alta queda bloqueada sin motivo.
   */
  const pvpEfectivo = useMemo(() => pvpEfectivoDe(sel), [sel])

  /** Alta = no tenía precio y ahora hay una propuesta. No es una actualización: es el primer precio. */
  const esAlta = useMemo(
    () => Boolean(sel) && Number(sel.pvp || 0) === 0 && Number(sel.pvp_propuesto || 0) > 0,
    [sel],
  )

  const claveTabla = (l) => `${l.tabla_sap}_${l.condicion}`

  // Lo que se ve en la tabla ES lo que se baja: una sola fuente para las dos cosas, así no puede
  // pasar que revises 84 líneas y bajes otras.
  const lineasSel = useMemo(() => lineas.filter((l) => !tablasOff[claveTabla(l)]), [lineas, tablasOff])

  /**
   * Las líneas que saldrían sin margen. Sirve para frenar la DESCARGA, que es lo que se carga en
   * SAP, sin frenar el cálculo, que es solo mirar. Con varias motos la llave del bloqueo lleva la
   * moto: que a una le falte el margen del 807 no frena el 807 de las otras. Con una sola, si sus
   * márgenes no se pudieron leer no sale ninguna.
   */
  const faltanMargen = () => (multi
    ? lineasSinMargen(lineasSel, margenesMulti, listas)
    : lineasSinMargenUna(lineasSel, margenes, listas, falloSingle || cargandoSingle))

  /** Lo que se revisa antes de bajar, igual para el XLSX y el TXT. */
  const puedeBajar = () => {
    if (!lineasSel.length) {
      toast.warn('No hay ningún archivo marcado.')
      return false
    }
    const problemas = validarLineas(lineasSel)
    const faltan = faltanMargen()
    if (!multi && faltan.length && (falloSingle || cargandoSingle)) {
      toast.error('No se puede bajar: no se pudieron leer los márgenes. Vuelve a elegir la moto.')
      return false
    }
    if (faltan.length) {
      // Se nombra la clasificación, no la condición: varias comparten 807 ZPAU y decir «ZPAU»
      // hacía creer que faltaba un margen que ya estaba puesto en otra clasificación.
      const que = multi
        ? [...new Set(faltan.map((l) => `${l.moto_label} · ${l.lista_label || l.condicion}`))].join(', ')
        : [...new Set(faltan.map((l) => l.lista_label || l.condicion))].join(', ')
      toast.error(`No se puede bajar: ${faltan.length} línea(s) saldrían sin margen `
        + `(${que}). `
        + 'Cárgales el % arriba o desmarca esas tablas.')
      return false
    }
    if (problemas.length) {
      toast.error(`Hay que corregir antes de descargar: ${problemas[0]}`)
      return false
    }
    return true
  }

  /**
   * Con varias motos puede haber fechas «válido desde» distintas (col_D). El TXT se nombra con la
   * fecha y la hoja del XLSX la lleva en la cabecera: juntarlas haría que el nombre y la cabecera
   * mintieran para parte de las líneas. Se baja una tanda por fecha, cada una con su fecha en el
   * nombre. Con una sola moto no cambia nada.
   */
  const tandas = () => (multi ? partirPorFecha(lineasSel) : [{ fecha: '', lineas: lineasSel }])

  const avisarTandas = (ts) => {
    if (ts.length > 1) {
      toast.info(`Las motos tienen ${ts.length} fechas de inicio distintas (${ts.map((t) => t.fecha).join(', ')}): `
        + 'se baja un juego de archivos por fecha.')
    }
  }

  const bajarXlsx = () => {
    if (!puedeBajar()) return
    const ts = tandas()
    avisarTandas(ts)
    const base = multi ? `${sels.length}_motos` : ((sel && sel.conca) || 'plano')
    let hojas = 0
    ts.forEach((t, i) => {
      const nombre = `PLANO_${base}_${new Date().toISOString().slice(0, 10)}${
        ts.length > 1 ? `_desde_${t.fecha}` : ''
      }`.replace(/[^\w-]/g, '_')
      hojas += agruparPorTabla(t.lineas).length
      // escalonado, igual que los TXT: varias descargas simultáneas las bloquea el navegador
      if (i === 0) descargarXlsx(t.lineas, nombre)
      else setTimeout(() => descargarXlsx(t.lineas, nombre), i * 600)
    })
    toast.success(ts.length > 1
      ? `${ts.length} XLSX (uno por fecha) con ${hojas} hoja(s) en total, una por tabla de SAP`
      : `XLSX con ${hojas} hoja(s), una por tabla de SAP`)
  }

  const bajarTxt = () => {
    if (!puedeBajar()) return
    const ts = tandas()
    avisarTandas(ts)
    let desfase = 0
    let n = 0
    ts.forEach((t) => {
      const archivos = descargarTxt(t.lineas, { delim, cols, cabecera, bom, desfase })
      desfase += archivos.length * 350
      n += archivos.length
    })
    toast.success(`${n} archivo(s) TXT — uno por tabla${ts.length > 1 ? ' y fecha' : ''}, en orden de carga`)
  }

  /**
   * Manda a SAP lo mismo que se bajaría: las líneas marcadas, con la misma validación de la descarga.
   * Las tablas desmarcadas viajan como clasificaciones excluidas. Solo van las motos que tienen líneas
   * en pantalla: una que no se pudo calcular podría tener líneas viejas guardadas.
   */
  const enviarSap = async () => {
    if (!puedeBajar()) return
    const grupoIds = [...new Set(lineasSel.map((l) => Number(multi ? l.moto_grupo_id : sel && sel.id)))]
      .filter((x) => x > 0)
    const excluidas = (listas || [])
      .filter((l) => tablasOff[`${l.tabla_sap}_${l.condicion}`])
      .map((l) => l.id)
    const tablas = agruparPorTabla(lineasSel).length
    const ok = await new Promise((resolve) => {
      Modal.confirm({
        title: 'Enviar el plano a SAP',
        content: `${lineasSel.length} línea(s) de ${grupoIds.length} moto(s), en ${tablas} envío(s): uno por tabla, `
          + 'en orden de carga. SAP avisa después si las aplicó.',
        okText: 'Enviar',
        cancelText: 'Cancelar',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })
    if (!ok) return
    setEnviandoSap(true)
    const r = await enviarPlanoSap({ grupos: grupoIds, excluidas })
    setEnviandoSap(false)
    if (!r || Number(r.success) !== 1) {
      toast.error((r && r.error) || 'No se pudo enviar el plano a SAP.')
      return
    }
    setLoteSap({ id: r.lote_id, modo: r.modo })
    setEnviosSap(r.envios || [])
    setRechazosSap({})
    const malos = (r.envios || []).filter((e) => e.estado === 'error_envio').length
    if (r.modo !== 'real') {
      toast.info(`Lote ${r.lote_id} armado en modo SIMULADO (sap_produccion = 0): no se llamó a SAP.`)
    } else if (malos) {
      toast.warn(`${malos} de ${(r.envios || []).length} envío(s) fallaron. Revísalos abajo y reintenta.`)
    } else {
      toast.success(`${(r.envios || []).length} envío(s) recibidos por SAP. Falta su confirmación.`)
    }
    onChanged?.()
  }

  const refrescarSap = async () => {
    if (!loteSap) return
    const r = await getEnviosSap({ loteId: loteSap.id })
    if (r && r.data) setEnviosSap(r.data)
  }

  const reenviarSap = async (envioId) => {
    const r = await reenviarEnvioSap(envioId)
    if (!r || Number(r.success) !== 1) {
      toast.error((r && r.error) || 'No se pudo reenviar.')
      return
    }
    setEnviosSap(r.envios || [])
  }

  const verRechazosSap = async (envioId) => {
    if (rechazosSap[envioId]) {
      setRechazosSap((p) => {
        const s = { ...p }
        delete s[envioId]
        return s
      })
      return
    }
    const r = await getEnvioSapLineas(envioId)
    const filas = ((r && r.data) || []).filter((l) => l.estado === 'rechazada')
    setRechazosSap((p) => ({ ...p, [envioId]: filas }))
  }

  const calcular = async () => {
    if (!sel) {
      toast.warn('Elige primero la moto')
      return
    }
    setIsLoading(true)
    const r = await calcularPlano(sel.id, org, 0)
    const fila = (r && r.data && r.data[0]) || null
    if (fila && fila.error) {
      toast.warn(fila.error)
      setResumen(null)
      setLineas([])
      setIsLoading(false)
      return
    }
    setResumen(fila)
    const ls = await getPlanoLineas(sel.id, 0)
    setLineas((ls && ls.data) || [])
    setTablasOff({})   // cada cálculo arranca con todos los archivos tildados
    setIsLoading(false)
    if (fila) toast.success(`${fila.lineas} líneas listas para cargar en SAP`)
    onChanged?.()
  }

  const cargarZdpc = async (g) => {
    if (!g) {
      setZdpc([])
      setZdpcEdits({})
      return
    }
    const r = await getGrupoZdpc(g.id)
    setZdpc((r && r.data) || [])
    setZdpcEdits({})
  }

  /**
   * Marcar o desmarcar un grupo de descuento cambia LAS LÍNEAS del plano (esa condición se codifica o
   * no se codifica), así que si ya estaba calculado se recalcula solo: no puede quedar un plano en
   * pantalla que no corresponda a lo marcado.
   */
  const toggleZdpc = async (z, incluida) => {
    if (!sel) return
    const r = await setGrupoZdpc(sel.id, z.lista_id, incluida)
    if (!r) {
      toast.error('No se pudo guardar el grupo de descuento')
      return
    }
    setZdpc((prev) => prev.map((x) => (
      x.lista_id === z.lista_id ? { ...x, incluida: incluida ? 1 : 0 } : x
    )))
    toast.success(incluida
      ? `${sel.modelo} entra al grupo ${z.grupo_descuento} (${pct(z.descuento_moto_pct)})`
      : `${sel.modelo} sale del grupo ${z.grupo_descuento}`)
    if (resumen) await calcular()
  }

  /**
   * El % de descuento de un grupo, para ESTA moto.
   *
   * Un ZDPC es una clasificación más, así que se guarda con el mismo escritor que los márgenes. Desde
   * f51 ese escritor escribe contra la clasificación editada, así que cambiar el descuento de un grupo
   * no toca a los otros. Lo que se escribe es una excepción de la moto: el % general del grupo queda
   * como estaba para las demás motos.
   */
  const guardarPctZdpc = async (z) => {
    const txt = String(zdpcEdits[z.lista_id] ?? '').trim()
    if (txt === '') return
    const num = Number(txt.replace(',', '.'))
    if (Number.isNaN(num) || num < 0 || num >= 100) {
      toast.warn('El descuento va entre 0 y 99,99 %.')
      return
    }
    setGuardandoZdpc(z.lista_id)
    const r = await setGrupoMargen({
      grupoId: sel.id,
      listaId: z.lista_id,
      valor: fraccion(num),
      nota: `Descuento del grupo ${z.grupo_descuento} para ${sel.modelo} ${sel.anio_modelo || ''}`.trim(),
    })
    setGuardandoZdpc(0)
    const d = (r && r.data && r.data[0]) || null
    if (!d || Number(d.result) === 0) {
      toast.error((d && d.error) || 'No se pudo guardar el descuento.')
      return
    }
    toast.success(`Grupo ${z.grupo_descuento}: descuento ${num}% para ${sel.modelo}.`)
    setZdpcEdits((prev) => {
      const sig = { ...prev }
      delete sig[z.lista_id]
      return sig
    })
    await cargarZdpc(sel)
    if (resumen) await calcular()
  }

  /** Los márgenes de ESTA moto, uno por clasificación, con de dónde sale cada uno. */
  const cargarMargenes = async (g) => {
    // Con turno, como la grilla: si se cambia de moto antes de que llegue la respuesta, la vieja
    // no puede aterrizar debajo de la moto nueva.
    turnoSingle.current += 1
    const turno = turnoSingle.current
    if (!g) {
      setMargenes([])
      setEdits({})
      setFalloSingle(false)
      setCargandoSingle(false)
      return
    }
    setCargandoSingle(true)
    const r = await getGrupoMargenes(g.id, {
      pvp: Number(g.pvp_propuesto || g.pvp || 0),
      ivaPct: Number(g.iva_pct || 0),
    })
    if (turno !== turnoSingle.current) return
    setMargenes((r && r.data) || [])
    setFalloSingle(!r)
    setCargandoSingle(false)
    setEdits({})
  }

  /**
   * Un margen de 0 % es válido (el precio a ese canal queda igual a la base), pero es fácil ponerlo
   * sin querer: antes de guardarlo se pide confirmación diciendo dónde queda en 0. Resuelve true si
   * no hay ceros o si se confirma.
   */
  const confirmarCeros = (donde = []) => new Promise((resolve) => {
    if (!donde.length) {
      resolve(true)
      return
    }
    Modal.confirm({
      title: `Vas a dejar ${donde.length === 1 ? 'un margen' : `${donde.length} márgenes`} en 0 %`,
      content: (
        <div>
          <div style={{ marginBottom: 6 }}>Con 0 % el precio de esa lista queda igual a la base, sin margen:</div>
          <ul style={{ margin: '0 0 0 18px', padding: 0, maxHeight: 220, overflowY: 'auto' }}>
            {donde.slice(0, 15).map((d) => <li key={d}>{d}</li>)}
          </ul>
          {donde.length > 15 && <div>… y {donde.length - 15} más</div>}
        </div>
      ),
      okText: 'Sí, guardar en 0 %',
      cancelText: 'Revisar',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })

  /**
   * Guarda solo lo que se tocó, cada valor en la MOTO para esa clasificación (nunca en otra: desde
   * f80 no hay herencia). Después se recarga TODO y se recalcula el plano, y el margen de las líneas
   * queda congelado con el valor nuevo, no con el que se calculó antes.
   */
  const guardarMargenes = async () => {
    if (!sel) return
    const cambios = Object.keys(edits)
      .map((k) => ({ listaId: Number(k), txt: String(edits[k]).trim() }))
      .filter((c) => c.txt !== '')
      .map((c) => ({ ...c, num: Number(c.txt.replace(',', '.')) }))

    if (!cambios.length) {
      toast.info('No cambiaste ningún margen.')
      return
    }
    if (cambios.some((c) => Number.isNaN(c.num) || c.num < 0 || c.num >= 100)) {
      toast.warn('El margen va entre 0 y 99,99 %.')
      return
    }
    const ceros = cambios.filter((c) => c.num === 0).map((c) => {
      const m = margenes.find((x) => Number(x.lista_id) === c.listaId)
      return `${sel.modelo} ${sel.anio_modelo || ''} · ${(m && m.lista_label) || `lista ${c.listaId}`}`
    })
    if (!(await confirmarCeros(ceros))) return

    setGuardandoM(true)
    const res = await Promise.all(cambios.map((c) => setGrupoMargen({
      grupoId: sel.id,
      listaId: c.listaId,
      valor: fraccion(c.num),
      nota: `Puesto al armar el plano · ${sel.modelo} ${sel.anio_modelo || ''}`.trim(),
    })))
    setGuardandoM(false)

    const ok = res.filter((r) => r && r.data && r.data[0] && Number(r.data[0].result) > 0).length
    if (!ok) {
      toast.error('No se pudo guardar el margen.')
      return
    }
    if (ok < cambios.length) toast.warn(`Se guardaron ${ok} de ${cambios.length} márgenes.`)
    else toast.success(`${ok} margen(es) guardados en ${sel.modelo}. Recalculando el plano.`)

    await cargarMargenes(sel)
    if (resumen) await calcular()
  }

  /* ------------------------------ varias motos ------------------------------ */

  /**
   * Los márgenes de cada moto elegida, de a una: son N lecturas y en paralelo competirían con el
   * resto de la pantalla. Si la de una moto falla, queda marcada (`fallo`) y frena la descarga y el
   * «Continuar» de esa moto: no saber su margen no es lo mismo que tenerlo.
   */
  const cargarMargenesMulti = async (gs) => {
    turnoMulti.current += 1
    const turno = turnoMulti.current
    if (!gs || gs.length < 2) {
      setMargenesMulti([])
      setEditsMulti({})
      setCargandoMulti(false)
      return []
    }
    setCargandoMulti(true)
    const datos = []
    // eslint-disable-next-line no-restricted-syntax
    for (const g of gs) {
      // eslint-disable-next-line no-await-in-loop
      const r = await getGrupoMargenes(g.id, {
        pvp: Number(g.pvp_propuesto || g.pvp || 0),
        ivaPct: Number(g.iva_pct || 0),
      })
      // null = esta carga quedó vieja: quien la esperaba no debe seguir con ella
      if (turno !== turnoMulti.current) return null
      datos.push({ grupo: g, margenes: (r && r.data) || [], fallo: !r })
    }
    setMargenesMulti(datos)
    setEditsMulti({})
    setCargandoMulti(false)
    return datos
  }

  const grilla = useMemo(() => construirGrilla(margenesMulti, listas), [margenesMulti, listas])

  // Lo que cambió de verdad: es el N del botón y lo que se manda.
  const cambiosMulti = useMemo(
    () => cambiosAItems(grilla, editsMulti, (g) => `Puesto al armar el plano · ${g.modelo} ${g.anio_modelo || ''}`.trim()),
    [grilla, editsMulti],
  )

  const etiquetaLista = (listaId) => {
    const c = grilla.columnas.find((x) => x.lista_id === Number(listaId))
    return (c && c.lista_label) || `lista ${listaId}`
  }

  const aplicarColumna = (listaId) => {
    const txt = String(aplicarTxt[listaId] ?? '').trim()
    const num = leerPct(txt)
    if (num === null) {
      toast.info('Escribe el % que quieres aplicar a todas.')
      return
    }
    if (!esPctValido(num)) {
      toast.warn('El margen va entre 0 y 99,99 %.')
      return
    }
    const r = aplicarAColumna(grilla, editsMulti, listaId, txt, soloVacias)
    setEditsMulti(r.edits)
    if (!r.aplicadas) {
      toast.info(soloVacias
        ? `Ninguna moto tiene ${etiquetaLista(listaId)} vacío: no se tocó nada.`
        : `${etiquetaLista(listaId)} no aplica a ninguna de estas motos.`)
      return
    }
    toast.info(`${etiquetaLista(listaId)}: ${num} % en ${r.aplicadas} moto(s). Falta «Guardar y calcular».`)
  }

  /**
   * Varias motos, un solo cálculo: moto por moto, en orden. El que falla no corta a los demás; al
   * final se dice cuáles no salieron y por qué (ej. 'El conca no tiene PVP asignado por Trade'). Las
   * motos sin PVP ni se mandan.
   */
  const calcularMulti = async () => {
    // La selección y la organización VIGENTES, no las del closure: se llama también al final de un
    // guardado, que pudo haber empezado con otras motos.
    const actuales = selsRef.current
    const orgActual = orgRef.current
    if (actuales.length < 2) return
    setIsLoading(true)
    const { lineas: ls, resultados } = await calcularVarias(actuales, orgActual, {
      calcular: calcularPlano,
      leerLineas: getPlanoLineas,
      onProgreso: (i, n) => setProgreso(i < n ? { hecho: i, total: n } : null),
    })
    setIsLoading(false)
    if (!mismaSeleccion(actuales, selsRef.current) || orgActual !== orgRef.current) {
      toast.info('Cambiaron las motos o la organización mientras se calculaba: ese cálculo se descartó.')
      return
    }
    setResultadosMulti(resultados)
    setLineas(ls)
    setTablasOff({})
    const ok = resultados.filter((r) => r.estado === 'ok').length
    const malas = resultados.filter((r) => r.estado !== 'ok')
    if (!ok) {
      toast.error(`Ninguna moto se pudo calcular. ${malas.map((r) => `${etiquetaMoto(r.grupo)}: ${r.error}`).join(' · ')}`)
    } else if (malas.length) {
      toast.warn(`${ok} de ${resultados.length} moto(s) calculadas (${ls.length} líneas). No salieron: `
        + `${malas.map((r) => `${etiquetaMoto(r.grupo)}: ${r.error}`).join(' · ')}`)
    } else {
      toast.success(`${ok} moto(s) calculadas: ${ls.length} líneas listas para cargar en SAP`)
    }
    onChanged?.()
  }

  /**
   * Guarda en una sola llamada (o en lotes, si pasan del tope del back) solo las celdas que cambiaron,
   * y después recalcula TODAS las motos elegidas: un margen heredado puede mover varias líneas, y el
   * plano en pantalla tiene que ser el de los márgenes nuevos. Lo que falló queda tecleado para
   * reintentar; lo que entró se relee del servidor.
   */
  const guardarMargenesMulti = async () => {
    const actuales = selsRef.current
    const { items, invalidos } = cambiosMulti
    if (invalidos.length) {
      toast.warn(`El margen va entre 0 y 99,99 %. Revisa:${invalidos.slice(0, 4)
        .map((i) => `${etiquetaMoto(i.grupo)} · ${i.lista_label}`).join(', ')}${invalidos.length > 4 ? '…' : ''}`)
      return
    }
    if (!items.length) {
      toast.info('No cambiaste ningún margen.')
      return
    }
    const nombreMoto = (grupoId) => etiquetaMoto(actuales.find((g) => Number(g.id) === Number(grupoId)))
    const ceros = items.filter((i) => Number(i.valor) === 0)
      .map((i) => `${nombreMoto(i.grupo_id)} · ${etiquetaLista(i.lista_id)}`)
    if (!(await confirmarCeros(ceros))) return
    setGuardandoM(true)
    const lotes = []
    // eslint-disable-next-line no-restricted-syntax
    for (const lote of enLotes(items)) {
      // eslint-disable-next-line no-await-in-loop
      lotes.push({ items: lote, resp: await setGrupoMargenesMasivo(lote) })
    }

    const { ok, total, fallas } = resumirLotes(lotes)
    const nombreDe = (grupoId) => etiquetaMoto(actuales.find((g) => Number(g.id) === Number(grupoId)))
    const detalle = fallas.slice(0, 6)
      .map((f) => `${nombreDe(f.grupo_id)} · ${etiquetaLista(f.lista_id)}: ${f.error}`)
      .join(' · ')
    const cola = fallas.length > 6 ? ` · y ${fallas.length - 6} más` : ''
    if (!ok) {
      setGuardandoM(false)
      toast.error(`0 de ${total} guardados. ${detalle}${cola}`)
      return
    }
    if (fallas.length) toast.warn(`${ok} de ${total} guardados. No entraron: ${detalle}${cola}`)
    else toast.success(`${ok} de ${total} guardados. Recalculando ${actuales.length} moto(s).`)

    const pendientes = {}
    fallas.forEach((f) => {
      const clave = `${Number(f.grupo_id)}_${Number(f.lista_id)}`
      if (editsMulti[clave] !== undefined) pendientes[clave] = editsMulti[clave]
    })
    // guardandoM sigue arriba hasta el final: la selección queda bloqueada durante la recarga y el
    // recálculo. Si aun así cambió (o la recarga quedó vieja), no se recalcula nada: el cálculo sería
    // de motos que ya no están elegidas.
    const datos = await cargarMargenesMulti(actuales)
    if (!datos || !mismaSeleccion(actuales, selsRef.current)) {
      setGuardandoM(false)
      return
    }
    setEditsMulti(pendientes)
    await calcularMulti()
    setGuardandoM(false)
  }

  // Las motos que frenan «Continuar»: alguna condición que aplica sin margen, o márgenes sin leer.
  const bloqueosMulti = useMemo(() => sinMargenPorMoto(margenesMulti, listas), [margenesMulti, listas])

  useEffect(() => {
    cargarMargenes(sel)
    cargarZdpc(sel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel])

  useEffect(() => {
    cargarMargenesMulti(sels.length > 1 ? sels : [])
    setAplicarTxt({})
  }, [sels])

  // Al cambiar de moto se limpia lo anterior: nunca mostrar el cálculo de otra moto.
  useEffect(() => {
    setLineas([])
    setResumen(null)
    setTablasOff({})
    setResultadosMulti([])
    setEnviosSap([])
    setLoteSap(null)
    setRechazosSap({})
  }, [sels, org])

  /**
   * Back arma el plano de lo que Trade mandó — ese sigue siendo el camino normal y el default.
   * Pero el plano no depende del ciclo: depende de que la moto TENGA PRECIO. Si una moto ya tiene
   * PVP definido y hay que regenerarle el plano (se cayó una carga, cambió una clasificación, un
   * margen nuevo), obligar a Trade a mandar una propuesta que nadie pidió es una traba, no un control.
   * Por eso el filtro se puede abrir: al abrirlo entran las motos con `pvp > 0` y NADA más — una moto
   * sin precio sigue sin poder generar plano, que era el riesgo original ("calcular una que nadie pidió").
   */
  const opciones = useMemo(() => {
    const permitidos = soloGrupos && soloGrupos.length ? new Set(soloGrupos.map(Number)) : null
    return grupos
      .filter((g) => {
        if (!permitidos) return true
        if (permitidos.has(Number(g.id))) return true
        return !soloTrade && Number(g.pvp) > 0
      })
      .map((g) => {
        const fueraDeTrade = Boolean(permitidos) && !permitidos.has(Number(g.id))
        return {
          ...g,
          fueraDeTrade,
          etiqueta: `${g.marca || ''} ${g.modelo} ${g.anio_modelo || ''} · ${g.materiales} color(es)${
            fueraDeTrade ? ' · sin propuesta de Trade' : ''
          }`.trim(),
        }
      })
  }, [grupos, soloGrupos, soloTrade])

  // cuántas vienen del ciclo de Trade, para poder rotular las dos cifras sin mentir
  const nDeTrade = useMemo(() => opciones.filter((o) => !o.fueraDeTrade).length, [opciones])

  // Si se vuelve a cerrar el filtro con una moto de afuera elegida, se limpia: dejarla seleccionada
  // pero invisible en la lista es la clase de estado fantasma que hace que alguien calcule a ciegas.
  const cambiarSoloTrade = (v) => {
    setSoloTrade(v)
    const quedan = sels.filter((s) => !s.fueraDeTrade)
    if (v && quedan.length !== sels.length) {
      setSels(quedan)
      setLineas([])
      setResumen(null)
    }
  }

  /**
   * Cascada: si se llega acá desde la bandeja, la moto —o las motos— vienen preseleccionadas. Se
   * toman de `opciones`, no de `grupos`, para que traigan su etiqueta y su marca de fuera de Trade.
   * Se aplica una vez por preselección: abrir o cerrar el filtro después no debe resetear la elección.
   * La preselección se reconoce por identidad: cada clic en la bandeja manda un arreglo nuevo, así que
   * volver a pedir las mismas motos después de cambiar la elección a mano sí vuelve a aplicarse.
   */
  const preFuente = preGrupoIds && preGrupoIds.length ? preGrupoIds : null
  useEffect(() => {
    if (!preFuente || !opciones.length || preAplicado.current === preFuente) return
    const ids = preFuente.map(Number).filter((x) => x > 0)
    const elegidas = ids.map((id) => opciones.find((o) => Number(o.id) === id)).filter(Boolean)
    if (!elegidas.length) return
    preAplicado.current = preFuente
    setSels(elegidas)
  }, [preFuente, opciones])

  // El procedimiento devuelve esta columna como `k`. Se llamaba `pv_sin_impuestos` hasta que f47
  // recreo las rutinas por el problema de charset y la renombro; el front siguio leyendo el nombre
  // viejo, asi que `undefined || 0` daba SIEMPRE cero. Se leen los dos por si convive una base con
  // la version anterior del procedimiento.
  const k = resumen
    ? Math.round(Number(resumen.k ?? resumen.pv_sin_impuestos ?? 0))
    : 0
  // La alerta se cuenta sobre lo TILDADO: si el archivo con la llave rota no se va a bajar, no hay
  // por qué frenar la descarga de los otros.
  /**
   * Las primeras líneas del TXT tal cual se van a escribir, por tabla. Es la forma de contestar los tres
   * supuestos (delimitador, A–F vs A–G, cabecera) SIN reunión: quien carga en LSMW lo mira y dice si calza.
   * La tabulación se pinta como ⇥ porque un TAB invisible es justo el detalle que no se puede verificar.
   */
  const preview = useMemo(() => {
    if (!verTxt || !lineasSel.length) return []
    return agruparPorTabla(lineasSel).map((g) => ({
      titulo: `${g.tabla} · ${g.condicion} · ${g.filas.length} registro(s)`,
      lineas: construirTxt(g, { delim, cols, cabecera })
        .split('\r\n')
        .slice(0, 4)
        .map((l, i) => ({
          id: `${g.tabla}-${g.condicion}-${i}`,
          esCabecera: cabecera && i === 0,
          txt: l.split(delim).join(delim === '\t' ? ' ⇥ ' : ` ${delim} `),
        })),
    }))
  }, [verTxt, lineasSel, delim, cols, cabecera])

  const conEspacio = lineasSel.filter((l) => Number(l.alerta_espacio) === 1).length

  /** Un archivo TXT por tabla+condición, con su conteo y su label de negocio. */
  const archivos = useMemo(() => {
    const m = new Map()
    lineas.forEach((l) => {
      const clave = claveTabla(l)
      if (!m.has(clave)) m.set(clave, { clave, tabla: l.tabla_sap, condicion: l.condicion, n: 0 })
      m.get(clave).n += 1
    })
    return [...m.values()]
  }, [lineas])

  const porTabla = useMemo(() => archivos.map((a) => [`${a.tabla} · ${a.condicion}`, a.n]), [archivos])

  const columns = useMemo(
    () => [
      // Con varias motos cada línea dice de cuál es; con una sola sobra.
      ...(multi
        ? [{
          id: 'moto',
          header: 'Moto',
          size: 150,
          accessorFn: (r) => `${r.moto_label || ''} ${r.moto_conca || ''}`,
          Cell: ({ row }) => (
            <div style={{ lineHeight: 1.25 }}>
              <div style={{ fontWeight: 600, color: '#0f2439' }}>{row.original.moto_label}</div>
              <div style={{ fontSize: 10.5, color: '#8a94a0' }}>{row.original.moto_conca}</div>
            </div>
          ),
        }]
        : []),
      // El nombre de negocio arriba y el código de SAP abajo, chico. Los códigos NO se quitan —quien
      // carga en SAP los cruza contra la plantilla— pero dejan de ser lo primero que se lee.
      {
        id: 'clasificacion',
        header: 'Clasificación',
        size: 250,
        accessorFn: (r) => r.lista_label || '',
        Cell: ({ row }) => (
          <div style={{ lineHeight: 1.25 }}>
            <div style={{ fontWeight: 600, color: '#0f2439' }}>{row.original.lista_label}</div>
            <div style={{ fontSize: 10.5, color: '#8a94a0' }}>
              tabla {row.original.tabla_sap} · {row.original.condicion}
              {row.original.transaccion ? ` · ${row.original.transaccion}` : ''}
            </div>
          </div>
        ),
      },
      { accessorKey: 'col_A', header: 'A · acción', size: 70 },
      { accessorKey: 'col_B', header: 'B · llave SAP', size: 220 },
      { accessorKey: 'col_C', header: 'C · válido hasta', size: 110 },
      { accessorKey: 'col_D', header: 'D · válido desde', size: 110 },
      {
        id: 'col_E',
        header: 'E · valor',
        size: 120,
        accessorFn: (r) => Number(r.col_E || 0),
        Cell: ({ row }) =>
          row.original.col_F === '%' ? `${Number(row.original.col_E || 0)} %` : fmt(row.original.col_E),
      },
      { accessorKey: 'col_F', header: 'F · moneda', size: 90 },
      // con qué margen se calculó la línea: queda congelado aunque después se mueva la regla
      {
        id: 'margen',
        header: 'Margen usado',
        size: 105,
        accessorFn: (r) => Number(r.margen_aplicado || 0),
        Cell: ({ row }) =>
          row.original.margen_aplicado === null || row.original.margen_aplicado === undefined
            ? <span style={{ color: '#9aa5b1' }}>—</span>
            : pct(row.original.margen_aplicado),
      },
      { accessorKey: 'material', header: 'Material', size: 110 },
    ],
    [multi],
  )

  // «Continuar» no deja pasar una moto con una condición sin margen: esas líneas saldrían a SAP en 0.
  // Con una sola moto también frena si sus márgenes no se pudieron leer o todavía no llegaron.
  const bloqueaContinuar = multi
    ? cargandoMulti || bloqueosMulti.length > 0
    : cargandoSingle || falloSingle || sinMargen.length > 0

  // Mientras se guarda, se leen márgenes o se calcula, la selección no se toca: lo que está en
  // vuelo terminaría pintando o calculando motos que ya no están elegidas.
  const ocupado = Boolean(progreso) || guardandoM || cargandoMulti

  const paso = (n, titulo, ayuda) => (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
      <span
        style={{
          background: '#0f2439',
          color: '#fff',
          width: 22,
          height: 22,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          flex: '0 0 auto',
        }}
      >
        {n}
      </span>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f2439' }}>{titulo}</div>
        {ayuda && <div style={{ fontSize: 12, color: '#6b7684', marginTop: 2 }}>{ayuda}</div>}
      </div>
    </div>
  )

  const caja = (label, valor, tono = '#0f2439') => (
    <div
      style={{
        border: '1px solid #e3e7ec',
        borderRadius: 8,
        padding: '8px 12px',
        minWidth: 132,
        background: '#fff',
      }}
    >
      <div style={{ fontSize: 10.5, color: '#6b7684', textTransform: 'uppercase', letterSpacing: '.02em' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: tono }}>{valor}</div>
    </div>
  )

  const flecha = <span style={{ color: '#9aa5b1', fontSize: 18, alignSelf: 'center' }}>→</span>

  return (
    <div>
      {/* ---------------- PASO 1: la moto ---------------- */}
      <div className="precios-card">
        {paso(
          1,
          'Elige la moto (o varias)',
          soloGrupos && soloGrupos.length
            ? soloTrade
              ? `Solo las ${nDeTrade} moto(s) de Trade sin publicar. Todos los colores van al mismo precio.`
              : `Las ${nDeTrade} de Trade más las que ya tienen precio (${opciones.length} en total).`
            : 'Todos los colores del modelo van al mismo precio.',
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={opciones}
            value={sels}
            onChange={(e, v) => setSels(v)}
            getOptionLabel={(o) => o.etiqueta || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                {o.etiqueta}
              </li>
            )}
            disabled={ocupado}
            size="small"
            sx={{ minWidth: 420, flex: '1 1 420px' }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Modelo y año"
                placeholder={sels.length ? '' : 'Puedes elegir varias. Ej: BLACK 2023'}
              />
            )}
          />
          <TextField
            select
            size="small"
            label="Organización de ventas"
            value={org}
            onChange={(e) => setOrg(e.target.value)}
            disabled={ocupado}
            SelectProps={{ native: true }}
            sx={{ minWidth: 190 }}
          >
            <option value="1000">1000</option>
            <option value="1100">1100</option>
          </TextField>
        </div>

        {soloGrupos && soloGrupos.length > 0 && (
          <FormControlLabel
            sx={{ mt: 0.5, '& .MuiFormControlLabel-label': { fontSize: 12.5, color: '#6b7684' } }}
            control={
              <Checkbox
                size="small"
                checked={!soloTrade}
                onChange={(e) => cambiarSoloTrade(!e.target.checked)}
              />
            }
            label="Mostrar también motos con precio que no vienen de Trade"
          />
        )}

        {sel && sel.fueraDeTrade && (
          <div
            style={{
              marginTop: 8,
              padding: '8px 12px',
              background: '#fff9ec',
              border: '1px solid #f0dfae',
              borderRadius: 6,
              fontSize: 12.5,
              color: '#7a5300',
            }}
          >
            Esta moto <b>no viene de Trade</b>. Revisa que su precio actual ({fmt(pvpEfectivo)}) sea el
            que quieres cargar en SAP.
          </div>
        )}

        {multi && sels.some((s) => s.fueraDeTrade) && (
          <div
            style={{
              marginTop: 8,
              padding: '8px 12px',
              background: '#fff9ec',
              border: '1px solid #f0dfae',
              borderRadius: 6,
              fontSize: 12.5,
              color: '#7a5300',
            }}
          >
            <b>{sels.filter((s) => s.fueraDeTrade).map(etiquetaMoto).join(', ')}</b>{' '}
            no vienen de una propuesta de Trade. Se arma el plano con el precio que ya tienen definido:
            revisa que ese sea el que quieres cargar en SAP.
          </div>
        )}

        {multi && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: '#6b7684' }}>
            <b>{sels.length} motos</b> ·{' '}
            {sels.reduce((n, s) => n + Number(s.materiales || 0), 0)} color(es) en total
          </div>
        )}

        {sel && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: '#6b7684' }}>
            Modelo <b>{sel.conca}</b> · {sel.materiales} color(es)
            {Number(sel.materiales_sin_precio) > 0 && (
              <span style={{ color: '#d64545' }}> · {sel.materiales_sin_precio} sin precio</span>
            )}
            {Number(sel.materiales_descuadrados) > 0 && (
              <span style={{ color: '#e3a300' }}>
                {' '}
                · {sel.materiales_descuadrados} con precio distinto al del modelo
              </span>
            )}
          </div>
        )}
      </div>

      {/* ---------------- PASO 2: el dato de Trade ---------------- */}
      {sel && (
        <div className="precios-card">
          {paso(2, 'El precio que puso Trade', 'De este precio salen todos los demás.')}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {caja(esAlta ? 'PVP nuevo (alta)' : 'PVP', fmt(pvpEfectivo))}
            {caja('IVA + impo. consumo', pct(sel.iva_pct))}
            {caja('Colores a cargar', sel.materiales)}
          </div>

          {falloSingle && !cargandoSingle && (
            <div
              style={{
                marginTop: 10, background: '#fdecec', border: '1px solid #f3b6b6', color: '#8f2d2d',
                borderRadius: 8, padding: '9px 12px', fontSize: 12.5,
              }}
            >
              <b>No se pudieron leer los márgenes de esta moto.</b> Descarga y «Continuar» quedan
              frenados. Vuelve a elegir la moto.
            </div>
          )}

          {/* ALTA: la moto no tenía precio. El plano se arma igual —es el mismo trámite— pero se dice
              que es el PRIMER precio, porque no hay con qué comparar y eso cambia qué hay que revisar. */}
          {esAlta && (
            <div
              style={{
                marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12.5,
                background: '#eef6ff', border: '1px solid #cfe2f7', color: '#0f2439',
              }}
            >
              <b>Precio nuevo:</b> no hay precio anterior para comparar. Revisa el <b>IVA</b> y los
              {' '}<b>márgenes</b> antes de publicar.
            </div>
          )}

          {pvpEfectivo === 0 && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: '#8f2d2d' }}>
              Esta moto todavía no tiene PVP <b>ni propuesta de Trade</b>. Trade lo tiene que registrar
              antes de armar el plano.
            </div>
          )}
          {/* Qué condiciones se van a generar, ANTES de calcular — y con el margen editable acá mismo.
              Es el único listado: el bloque duplicado del paso 3 se eliminó. */}
          {margenesAplican.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2439' }}>
                Se van a generar líneas para estas {margenesAplican.length} condiciones
              </div>
              <div style={{ fontSize: 12, color: '#6b7684', margin: '4px 0 8px' }}>
                Margen de esta moto y precio facturado. <b>Clic en una tarjeta para cambiar el %.</b>
              </div>
              {sinMargen.length > 0 && (
                <div
                  style={{
                    background: '#fdecec', border: '1px solid #f3b6b6', color: '#8f2d2d',
                    borderRadius: 8, padding: '9px 12px', fontSize: 12.5, marginBottom: 10,
                  }}
                >
                  <b>Faltan márgenes</b>
                  {': '}
                  <b>{sinMargen.map((m) => m.lista_label).join(' · ')}</b>.
                  {' '}Pon el % en la tarjeta y <b>Guardar y recalcular</b>; sin eso no se descarga.
                </div>
              )}
              <MargenTarjetas
                items={margenesAplican}
                editable
                edits={edits}
                onEditChange={(listaId, valor) => setEdits({ ...edits, [listaId]: valor })}
              />
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={guardarMargenes}
                  disabled={guardandoM || !Object.keys(edits).length}
                >
                  {guardandoM ? 'Guardando…' : 'Guardar y recalcular'}
                </Button>
                <span style={{ fontSize: 11.5, color: '#6b7684' }}>
                  Solo cambia <b>esa</b> clasificación; las demás no se mueven.
                </span>
              </div>
            </div>
          )}

          {/* Grupos de descuento: decisión de Back Comercial. Antes se marcaba en la pantalla de
              Trade, y no correspondía: no es el precio, es qué condiciones se codifican. */}
          {zdpc.length > 0 && (
            <div style={{ marginTop: 16, borderTop: '1px solid #e3e7ec', paddingTop: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#0f2439' }}>
                ¿Esta moto entra en grupos de descuento?
              </div>
              <div style={{ fontSize: 12, color: '#6b7684', margin: '4px 0 8px' }}>
                Almacenes que venden la moto a crédito. Sin marcar, <b>no sale en el plano</b>. El % es
                solo <b>de esta moto</b>.
              </div>
              {zdpc.map((z) => {
                // El % que de verdad se aplica a esta moto (f52). Antes acá se mostraba el general
                // del grupo, que podía no ser el que usaba el plano.
                const propio = Number(z.regla_propia_id || 0) > 0
                const efectivo = Number(z.descuento_moto_pct || 0)
                const general = Number(z.descuento_pct || 0)
                const tocado = zdpcEdits[z.lista_id] !== undefined
                return (
                  <div
                    key={z.lista_id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                      padding: '3px 0',
                    }}
                  >
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={Number(z.incluida) === 1}
                          onChange={(e) => toggleZdpc(z, e.target.checked)}
                        />
                      }
                      label={
                        <span style={{ fontSize: 12.5 }}>
                          Grupo <b>{z.grupo_descuento}</b>
                          <span style={{ color: '#6b7684' }}> · {z.clientes || 'sin clientes asignados'}</span>
                        </span>
                      }
                    />
                    <TextField
                      size="small"
                      label="Descuento %"
                      value={tocado
                        ? zdpcEdits[z.lista_id]
                        : String(Number((efectivo * 100).toFixed(2)))}
                      onChange={(e) => setZdpcEdits({ ...zdpcEdits, [z.lista_id]: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') guardarPctZdpc(z)
                      }}
                      sx={{ width: 130 }}
                      InputProps={{ endAdornment: <span style={{ fontSize: 12, color: '#6b7684' }}>%</span> }}
                    />
                    {tocado && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={guardandoZdpc === z.lista_id}
                        onClick={() => guardarPctZdpc(z)}
                      >
                        {guardandoZdpc === z.lista_id ? 'Guardando…' : 'Guardar'}
                      </Button>
                    )}
                    <span style={{ fontSize: 11.5, color: propio ? '#8a5a00' : '#6b7684' }}>
                      {propio
                        ? `propio de esta moto · el grupo en general va ${pct(general)}`
                        : `del grupo en general (${pct(general)})`}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <Button variant="contained" onClick={calcular} disabled={pvpEfectivo === 0}>
              {esAlta ? 'Armar el plano del precio nuevo' : 'Calcular el plano'}
            </Button>
          </div>
        </div>
      )}

      {/* ---------------- PASO 2 con varias motos: márgenes en grilla ---------------- */}
      {multi && (
        <div className="precios-card">
          {paso(
            2,
            `Los márgenes de las ${sels.length} motos`,
            'Margen por moto y condición. «Aplicar a todas» pone el mismo % en la columna.',
          )}

          {cargandoMulti && (
            <div style={{ fontSize: 12.5, color: '#6b7684', marginBottom: 8 }}>Leyendo los márgenes de cada moto…</div>
          )}

          {!cargandoMulti && bloqueosMulti.length > 0 && (
            <div
              style={{
                background: '#fdecec', border: '1px solid #f3b6b6', color: '#8f2d2d',
                borderRadius: 8, padding: '9px 12px', fontSize: 12.5, marginBottom: 10,
              }}
            >
              <b>Faltan márgenes en {bloqueosMulti.length} moto(s)</b>:
              <ul style={{ margin: '4px 0 4px 18px', padding: 0 }}>
                {bloqueosMulti.map((b) => (
                  <li key={b.grupo.id}>
                    <b>{etiquetaMoto(b.grupo)}</b>
                    {': '}
                    {b.sinLeer
                      ? 'no se pudieron leer sus márgenes (recarga la selección)'
                      : b.faltan.map((m) => m.lista_label).join(' · ')}
                  </li>
                ))}
              </ul>
              Cárgales el % en la grilla; sin eso no se descarga ni se continúa.
            </div>
          )}

          {!cargandoMulti && (
            <MargenGrilla
              grilla={grilla}
              edits={editsMulti}
              onEditChange={(clave, valor) => setEditsMulti((prev) => ({ ...prev, [clave]: valor }))}
              aplicar={aplicarTxt}
              onAplicarChange={(listaId, valor) => setAplicarTxt((prev) => ({ ...prev, [listaId]: valor }))}
              onAplicar={aplicarColumna}
              soloVacias={soloVacias}
              onSoloVacias={setSoloVacias}
              disabled={guardandoM}
            />
          )}

          {Object.keys(editsMulti).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Button size="small" variant="text" onClick={() => setEditsMulti({})} disabled={guardandoM}>
                Descartar cambios
              </Button>
            </div>
          )}

          <div style={{ marginTop: 14, fontSize: 12, color: '#6b7684' }}>
            Grupos de descuento (816 ZDPC): <b>selecciona una sola moto</b> para marcarlos.
          </div>

          <div style={{ marginTop: 14 }}>
            {/* Un solo botón: con márgenes sin guardar en la grilla primero los guarda (y eso ya
                recalcula); sin cambios solo calcula. Así no se puede calcular con márgenes viejos
                mientras en pantalla se ven los nuevos. */}
            <Button
              variant="contained"
              onClick={cambiosMulti.items.length ? guardarMargenesMulti : calcularMulti}
              disabled={Boolean(progreso) || cargandoMulti || guardandoM}
            >
              {progreso
                ? `Calculando ${progreso.hecho + 1} de ${progreso.total}…`
                : guardandoM
                  ? 'Guardando…'
                  : cambiosMulti.items.length
                    ? `Guardar y calcular (${sels.length} motos · ${cambiosMulti.items.length} cambio${
                      cambiosMulti.items.length === 1 ? '' : 's'})`
                    : `Calcular (${sels.length})`}
            </Button>
            <span style={{ fontSize: 12, color: '#6b7684', marginLeft: 10 }}>
              Si una falla, las demás siguen.
              {sels.some((s) => pvpEfectivoDe(s) === 0) && (
                <span style={{ color: '#8f2d2d' }}>
                  {' '}Sin PVP ni propuesta de Trade (se saltan):{' '}
                  <b>{sels.filter((s) => pvpEfectivoDe(s) === 0).map(etiquetaMoto).join(', ')}</b>.
                </span>
              )}
            </span>
          </div>
        </div>
      )}

      {/* ---------------- PASO 3 con varias motos: cómo salió cada una ---------------- */}
      {multi && resultadosMulti.length > 0 && (
        <div className="precios-card">
          {paso(3, 'Así salió cada moto')}
          <div style={{ overflowX: 'auto', border: '1px solid #e3e7ec', borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  {['Moto', 'Modelo', 'PVP', 'IVA', 'Sin impuestos (K)', 'Líneas', 'Resultado'].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '7px 9px', fontSize: 10.5, fontWeight: 700, color: '#56606b',
                        textTransform: 'uppercase', letterSpacing: '.4px', background: '#f4f6f8',
                        borderBottom: '1px solid #e3e7ec', textAlign: 'left', whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resultadosMulti.map((r, i) => {
                  const f = r.fila || {}
                  const td = { padding: '6px 9px', fontSize: 12, color: '#2b3440', borderBottom: '1px solid #eef1f4' }
                  return (
                    <tr key={r.grupo.id} style={{ background: i % 2 ? '#fafbfc' : '#fff' }}>
                      <td style={{ ...td, fontWeight: 600 }}>{etiquetaMoto(r.grupo)}</td>
                      <td style={td}>{r.grupo.conca}</td>
                      <td style={td}>{r.estado === 'ok' ? fmt(f.pvp) : '—'}</td>
                      <td style={td}>{r.estado === 'ok' ? pct(f.iva_pct) : '—'}</td>
                      <td style={{ ...td, color: '#1f9d55' }}>
                        {r.estado === 'ok' ? fmt(Math.round(Number(f.k ?? f.pv_sin_impuestos ?? 0))) : '—'}
                      </td>
                      <td style={td}>{r.estado === 'ok' ? r.lineas : '—'}</td>
                      <td style={{ ...td, color: r.estado === 'ok' ? '#1f9d55' : '#8f2d2d' }}>
                        {r.estado === 'ok' ? 'calculada' : (r.estado === 'saltada' ? `saltada: ${r.error}` : r.error)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {porTabla.map(([t, n]) => (
              <span
                key={t}
                style={{
                  background: '#eef0f2',
                  color: '#0f2439',
                  padding: '3px 10px',
                  borderRadius: 5,
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                {t}: {n} línea(s)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- PASO 3: cómo se calcula ---------------- */}
      {resumen && (
        <div className="precios-card">
          {paso(3, 'Así se calcula')}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {caja('PVP', fmt(resumen.pvp))}
            {flecha}
            {caja(`quitar IVA (${pct(resumen.iva_pct)})`, `÷ ${(1 + Number(resumen.iva_pct)).toFixed(2)}`, '#6b7684')}
            {flecha}
            {caja('Precio sin impuestos (K)', fmt(k), '#1f9d55')}
            {flecha}
            {caja('× (1 − margen de la lista)', 'por cada lista', '#6b7684')}
          </div>
          <div style={{ fontSize: 12.5, color: '#6b7684' }}>
            El margen es <b>lo que gana el cliente</b>. Leticia y San Andrés no ganan margen sobre el flete.
          </div>


          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {porTabla.map(([t, n]) => (
              <span
                key={t}
                style={{
                  background: '#eef0f2',
                  color: '#0f2439',
                  padding: '3px 10px',
                  borderRadius: 5,
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                {t}: {n} línea(s)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- PASO 4: qué se carga en SAP ---------------- */}
      {lineas.length > 0 && (
        <div className="precios-card">
          {paso(
            4,
            `Esto es lo que se carga en SAP (${
              lineasSel.length === lineas.length
                ? `${lineas.length} líneas`
                : `${lineasSel.length} de ${lineas.length} líneas`
            })`,
            'En el orden en que se cargan las tablas.',
          )}

          {/* Qué archivos entran. Se calculó todo; acá se elige qué se revisa y qué se baja. */}
          {archivos.length > 1 && (
            <div
              style={{
                border: '1px solid #e3e7ec',
                borderRadius: 8,
                background: '#fbfcfd',
                padding: '8px 12px 4px',
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 12, color: '#6b7684', marginBottom: 2 }}>
                Archivos a generar (desmarca los que no necesites):
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                {archivos.map((a) => (
                  <FormControlLabel
                    key={a.clave}
                    sx={{ mr: 2, '& .MuiFormControlLabel-label': { fontSize: 12.5 } }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!tablasOff[a.clave]}
                        onChange={(e) =>
                          setTablasOff((prev) => ({ ...prev, [a.clave]: !e.target.checked }))}
                      />
                    }
                    label={<span><b>{a.tabla}</b> · {a.condicion} <span style={{ color: '#8a94a0' }}>({a.n})</span></span>}
                  />
                ))}
                <Button size="small" onClick={() => setTablasOff({})} sx={{ ml: 'auto', fontSize: 11.5 }}>
                  Marcar todos
                </Button>
              </div>
            </div>
          )}
          {conEspacio > 0 && (
            <div
              style={{
                background: '#fdecec',
                border: '1px solid #f3b6b6',
                color: '#8f2d2d',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 12.5,
                marginBottom: 12,
              }}
            >
              {conEspacio} línea(s) tienen un espacio en la llave. Eso da error al cargar en SAP — hay que
              corregir el código del material antes de cargar.
            </div>
          )}
          <TablaPrecios columns={columns} data={lineasSel} isLoading={isLoading} pageSize={20} />

          {/* Cierre de la cascada: de acá pasa directo a Listos, sin volver a buscar la moto */}
          {onContinuar && (
            <div
              style={{
                marginTop: 14,
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                flexWrap: 'wrap',
                borderTop: '1px solid #e3e7ec',
                paddingTop: 14,
              }}
            >
              <Button
                variant="contained"
                disabled={bloqueaContinuar}
                onClick={() => onContinuar(multi ? sels : sel)}
              >
                Continuar → listos para publicar
              </Button>
              {!bloqueaContinuar && (
                <span style={{ fontSize: 12.5, color: '#6b7684' }}>
                  Descripción y códigos se completan solos. Falta publicar.
                </span>
              )}
              {bloqueaContinuar && (
                <span style={{ fontSize: 12.5, color: '#8f2d2d' }}>
                  Faltan márgenes:{' '}
                  {multi
                    ? (cargandoMulti
                      ? 'todavía se están leyendo'
                      : bloqueosMulti.map((b) => `${etiquetaMoto(b.grupo)}: ${
                        b.sinLeer ? 'márgenes sin leer' : b.faltan.map((m) => m.lista_label).join(', ')
                      }`).join(' · '))
                    : (cargandoSingle
                      ? 'todavía se están leyendo'
                      : (falloSingle
                        ? 'no se pudieron leer'
                        : sinMargen.map((m) => m.lista_label).join(', ')))}
                </span>
              )}
            </div>
          )}

          {/* ---- Descargas ---- */}
          <div style={{ marginTop: 16, borderTop: '1px solid #e3e7ec', paddingTop: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#0f2439', marginBottom: 4 }}>
              Descargar el plano
            </div>
            <div style={{ fontSize: 12, color: '#6b7684', marginBottom: 10 }}>
              <b>TXT</b>: un archivo por tabla, en orden de carga. <b>XLSX</b>: la plantilla de trabajo.
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="contained" onClick={bajarTxt} disabled={!lineasSel.length}>
                Descargar TXT para LSMW ({agruparPorTabla(lineasSel).length} archivos)
              </Button>
              <Button variant="outlined" onClick={bajarXlsx} disabled={!lineasSel.length}>
                Descargar XLSX (plantilla)
              </Button>
              <TextField
                select
                size="small"
                label="Delimitador"
                value={delim}
                onChange={(e) => setDelim(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: 150 }}
              >
                <option value={'\t'}>Tabulación</option>
                <option value=";">Punto y coma</option>
                <option value="|">Pipe</option>
              </TextField>
              <TextField
                select
                size="small"
                label="Columnas"
                value={cols}
                onChange={(e) => setCols(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: 130 }}
              >
                <option value="AF">A a F</option>
                {/* Antes era «A a H». La columna H (GRUPO Precio) se sacó del plano, así que el
                    tramo largo llega hasta G. */}
                <option value="AG">A a G</option>
              </TextField>
              <TextField
                select
                size="small"
                label="Fila de cabecera"
                value={cabecera ? 'si' : 'no'}
                onChange={(e) => setCabecera(e.target.value === 'si')}
                SelectProps={{ native: true }}
                sx={{ minWidth: 150 }}
              >
                <option value="si">Con cabecera</option>
                <option value="no">Sin cabecera</option>
              </TextField>
              <TextField
                select
                size="small"
                label="Tildes compatibles con Excel"
                value={bom ? 'si' : 'no'}
                onChange={(e) => setBom(e.target.value === 'si')}
                SelectProps={{ native: true }}
                sx={{ minWidth: 130 }}
              >
                <option value="no">No</option>
                <option value="si">Sí</option>
              </TextField>
              <Button size="small" variant="text" onClick={() => setVerTxt((v) => !v)}>
                {verTxt ? 'Ocultar el TXT' : 'Ver cómo va a quedar el TXT'}
              </Button>
            </div>

            {verTxt && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11.5, color: '#6b7684', marginBottom: 6 }}>
                  Primeras líneas de cada tabla, tal como quedan en el archivo (<b>⇥</b> = tabulación).
                </div>
                <div
                  style={{
                    maxHeight: 280,
                    overflow: 'auto',
                    background: '#0f2439',
                    borderRadius: 8,
                    padding: '10px 12px',
                  }}
                >
                  {preview.map((g) => (
                    <div key={g.titulo} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10.5, color: '#8fb4e0', textTransform: 'uppercase', marginBottom: 3 }}>
                        {g.titulo}
                      </div>
                      {g.lineas.map((l) => (
                        <div
                          key={l.id}
                          style={{
                            fontFamily: 'Consolas, monospace',
                            fontSize: 11.5,
                            color: l.esCabecera ? '#ffd479' : '#e8eef5',
                            whiteSpace: 'pre',
                            lineHeight: 1.5,
                          }}
                        >
                          {l.txt}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ fontSize: 11.5, color: '#9aa5b1', marginTop: 8 }}>
              Los valores por defecto equivalen a guardar la plantilla como .txt.
            </div>
          </div>

          {/* ---- Envío a SAP por API (f109). Convive con la descarga ---- */}
          <div style={{ marginTop: 16, borderTop: '1px solid #e3e7ec', paddingTop: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#0f2439', marginBottom: 4 }}>
              Enviar a SAP
            </div>
            <div style={{ fontSize: 12, color: '#6b7684', marginBottom: 10 }}>
              Manda las mismas líneas marcadas, un envío por tabla y en orden de carga. SAP confirma después
              cuáles aplicó; lo que rechace aparece acá con el motivo.
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={enviarSap}
                disabled={!lineasSel.length || enviandoSap}
              >
                {enviandoSap ? 'Enviando…' : `Enviar a SAP (${agruparPorTabla(lineasSel).length} envíos)`}
              </Button>
              {loteSap && (
                <>
                  <span style={{ fontSize: 12.5, color: loteSap.modo === 'real' ? '#1e6b3a' : '#8a5a00' }}>
                    Lote {loteSap.id} · {loteSap.modo === 'real' ? 'envío real' : 'SIMULADO (no se llamó a SAP)'}
                  </span>
                  <Button size="small" variant="text" onClick={refrescarSap}>Actualizar estado</Button>
                </>
              )}
            </div>

            {enviosSap.length > 0 && (
              <table style={{ width: '100%', marginTop: 12, borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#56606b', borderBottom: '1px solid #e3e7ec' }}>
                    <th style={{ padding: '6px 8px' }}>#</th>
                    <th style={{ padding: '6px 8px' }}>Envío</th>
                    <th style={{ padding: '6px 8px' }}>Líneas</th>
                    <th style={{ padding: '6px 8px' }}>Estado</th>
                    <th style={{ padding: '6px 8px' }}>Detalle</th>
                    <th style={{ padding: '6px 8px' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {enviosSap.map((e) => {
                    const color = {
                      confirmado: '#1e6b3a',
                      recibido: '#1f4e8c',
                      simulado: '#8a5a00',
                      parcial: '#b26a00',
                      rechazado: '#b42318',
                      error_envio: '#b42318',
                    }[e.estado] || '#56606b'
                    const rech = Number(e.rechazadas || 0)
                    return (
                      <React.Fragment key={e.envio_id}>
                        <tr style={{ borderBottom: '1px solid #f0f2f5' }}>
                          <td style={{ padding: '6px 8px' }}>{e.orden}</td>
                          <td style={{ padding: '6px 8px', fontFamily: 'Consolas, monospace' }}>
                            {e.tabla} / {e.condicion}
                            <div style={{ fontSize: 11, color: '#9aa5b1' }}>{e.envio_id}</div>
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {e.aplicadas !== null && e.aplicadas !== undefined && e.estado !== 'recibido'
                              ? `${e.aplicadas} de ${e.lineas}` : e.lineas}
                          </td>
                          <td style={{ padding: '6px 8px', color, fontWeight: 600 }}>
                            {e.estado.replace('_', ' ')}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#56606b', maxWidth: 360 }}>
                            {e.estado === 'error_envio'
                              ? `${e.http_status ? `HTTP ${e.http_status}: ` : ''}${e.ultimo_error || ''}`
                              : (e.recepcion_id || '')}
                            {rech > 0 && (
                              <Button size="small" variant="text" onClick={() => verRechazosSap(e.envio_id)}>
                                {rechazosSap[e.envio_id] ? 'Ocultar' : `Ver ${rech} rechazada(s)`}
                              </Button>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {['error_envio', 'simulado'].includes(e.estado) && (
                              <Button size="small" variant="outlined" onClick={() => reenviarSap(e.envio_id)}>
                                Reintentar
                              </Button>
                            )}
                          </td>
                        </tr>
                        {rechazosSap[e.envio_id] && (
                          <tr>
                            <td colSpan={6} style={{ padding: '4px 8px 10px', color: '#8f2d2d' }}>
                              {rechazosSap[e.envio_id].map((l) => (
                                <div key={`${l.llave_sap}_${l.material}`}>
                                  <b>{l.lista_label}</b> · {l.material} · <code>{l.llave_sap}</code>
                                  {' — '}{l.codigo}: {l.motivo}
                                </div>
                              ))}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PlanoWorkbench
